import React, { useCallback, useEffect } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import DateTimeInputFormik from "../../../components/UI/DateTimeInput/DateTimeInputFormik";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import HtmlEditor from "../../../components/UI/HtmlEditorInput/HtmlEditor";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";
import { validCpf, validTelefone } from "./EfisValidations";
import TextInput from "./TextInput";
import SelectAutocompleteFormik from "../../../components/UI/SelectAutocomplete/SelectAutocompleteFormik";
import SelectFileInput from "../../../components/UI/SelectFileInput/SelectFileInput";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";

const validationSchema = Yup.object().shape({
  nome: Yup.string().default("").min(1, "Nome muito curto").max(50, "Nome muito longo").required("Informe o nome"),
  cpf: Yup.mixed().default("").required("Informe o CPF").test(validCpf),
  email: Yup.string().default("").email("E-mail inválido").required("Informe o e-mail"),
  telefone: Yup.mixed().default("").required("Informe o telefone").test(validTelefone),
  nascimento: Yup.string().default("").required("Informe uma data de nascimento válida"),
  agendamento: Yup.string().default("").required("Data e horário não informados para o agendamento"),
  frutaPrediletaId: Yup.string().default("").required("Selecione sua fruta predileta"),
  relatorio1: Yup.string().default("").required("Informe um relatório da situação atual"),
  relatorio2: Yup.string().default("").required("Informe um relatório da situação encontrada"),
  arquivos: Yup.array().max(3, "No máximo 3 arquivos podem ser submetidos. ").min(1, "Selecione ao menos 1 arquivo"),
});

const initialValues = {
  nome: "",
  cpf: "",
  email: "",
  telefone: "",
  nascimento: "",
  agendamento: "",
  frutaPrediletaId: "",
  relatorio1: "",
  relatorio2: "",
  arquivos: [],
};

const frutas = [
  { key: "1", label: "Maça" },
  { key: "2", label: "Banana" },
  { key: "3", label: "Uva" },
  { key: "4", label: "Pera" },
  { key: "5", label: "Manga" },
  { key: "6", label: "Kiwi" },
  { key: "7", label: "Morango" },
  { key: "8", label: "Côco" },
  { key: "9", label: "Tomate" },
  { key: "10", label: "Abacaxi" },
];

const ExemploFormSimples = () => {
  const { request, data, errors, requestId, isLoading } = useApiClient();
  const history = useHistory();

  const handleSubmit = useCallback(
    (values) => {
      request(apiRoutes.cadastros.credenciamento.dummyPost, values);
    },
    [request],
  );

  const loadHandler = useCallback(() => {
    request(apiRoutes.cadastros.credenciamento.dummyGet, null, { requestId: "load" });
  }, [request]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    if ((data || errors) && !isLoading && formik.isSubmitting) {
      formik.setSubmitting(false);
    }

    if (requestId === "load" && data && !formik.values.loaded) {
      formik.setValues({ ...data.result, loaded: true });
    }

    if (errors && !isLoading) formik.setErrors({ ...formik.errors, ...errors });
  }, [formik, data, errors, requestId, isLoading]);

  return (
    <div style={{ backgroundColor: "white", padding: "20px" }}>
      <h1>Exemplo usando Formik + Yup</h1>
      <FormAlert formik={formik} data={data} errors={errors} isLoading={isLoading} />
      <Form noValidate onSubmit={formik.handleSubmit}>
        <Row>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Nome</Form.Label>
            <TextInput formik={formik} field="nome" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>CPF</Form.Label>
            <TextInput formik={formik} field="cpf" maxLength={11} />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Data de Nascimento</Form.Label>
            <DateTimeInputFormik formik={formik} field="nascimento" />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Email</Form.Label>
            <TextInput formik={formik} field="email" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Telefone</Form.Label>
            <TextInput formik={formik} field="telefone" maxLength={11} />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4}>
            <Form.Label>Agendamento</Form.Label>
            <DateTimeInputFormik formik={formik} field="agendamento" pickTime />
          </Form.Group>
          <Form.Group as={Col} sm={12} md={4} className="form-group-autocomplete">
            <Form.Label>Fruta Predileta</Form.Label>
            <SelectAutocompleteFormik
              formik={formik}
              field="frutaPrediletaId"
              maxOptions={5}
              optionValue="key"
              optionLabel="label"
              options={frutas}
            />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12} md={6}>
            <Form.Label>Texto Formatado 1</Form.Label>
            <HtmlEditor formik={formik} field="relatorio1" />
          </Form.Group>
          <Form.Group as={Col} xs={12} md={6}>
            <Form.Label>Texto Formatado 2</Form.Label>
            <HtmlEditor formik={formik} field="relatorio2" />
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12}>
            <SelectFileInput formik={formik} field="arquivos" save multiple />
          </Form.Group>
        </Row>
        <br />
        <Row>
          <Col>
            <Button
              variant="primary"
              disabled={formik.isSubmitting}
              onClick={() => {
                console.log(formik);
                formik.handleSubmit();
              }}
            >
              Enviar
            </Button>{" "}
            <Button variant="secondary" onClick={formik.handleReset} disabled={formik.isSubmitting || isLoading}>
              Limpar
            </Button>{" "}
            <Button variant="secondary" onClick={loadHandler} disabled={formik.isSubmitting || isLoading}>
              Carregar
            </Button>{" "}
            <Button variant="secondary" onClick={() => history.goBack()} disabled={formik.isSubmitting || isLoading}>
              Voltar
            </Button>{" "}
            <TextSpinner text="Enviando dados..." show={formik.isSubmitting} />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ExemploFormSimples;
